import { Checkbox } from '@hologram-dimension/checkbox';
import { InlineNotification } from '@hologram-dimension/inline-notification';
import useSimStatuses from 'api/sims/useSimStatuses';
import useTags from 'api/tags/useTags';
import { filterLabels } from 'components/SimInventoryFilters/shared';
import isArray from 'lodash/isArray';
import {
  type ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import type { OnSimsModified } from 'types/common';
import FilterAccordion, { type FilterProps } from '../../FilterAccordion/FilterAccordion';
import filterStyles from '../filterStyles.module.css';
import styles from './SimTagFilter.module.css';

interface SimTagFilterProps extends FilterProps {}

const SimTagFilter = forwardRef<OnSimsModified, SimTagFilterProps>(
  ({ form, onClear, segmentName, submit }, ref) => {
    const { register, setValue, getValues } = form;
    const { tags: storedSimTags, organizationId: filterOrganizationId } =
      useSimInventoryStoredFilters();

    const defaultOrgId = useOrganization();
    const organizationId = segmentName === 'all' ? filterOrganizationId : defaultOrgId;
    const hasOrganizationId = !!organizationId;

    const {
      networkStatusGroups,
      isLoading: statusesLoading,
      error: statusesError,
    } = useSimStatuses();

    // TODO: When/if we sync up filters to each other, then we'd delete this and use whatever is set
    const defaultStatuses = useMemo(() => {
      if (segmentName === 'all') {
        return undefined;
      }

      if (segmentName === 'active') {
        return networkStatusGroups?.active;
      }
      if (segmentName === 'preflight') {
        return networkStatusGroups?.predeployment;
      }
      return networkStatusGroups?.deactivated;
    }, [networkStatusGroups, segmentName]);

    // TODO: Organization ID from filters?
    // Note: is known that `LegacyTag` differs from `StructTag`, however that is intentional: if we typegen and the return mismatches from SIM Inventory: we will find that out here.
    const {
      tags,
      isLoading: tagsLoading,
      error: tagsError,
      mutate: mutateTags,
    } = useTags({
      filters: {
        organizationIds: hasOrganizationId ? [organizationId] : undefined,
        networkStatus: defaultStatuses,
      },
      canLoad: hasOrganizationId,
    });

    // Refresh
    const refresh = useCallback(() => {
      if (!tagsLoading) {
        mutateTags();
      }
    }, [mutateTags, tagsLoading]);

    useImperativeHandle(ref, () => refresh, [refresh]);

    const validTags = useMemo(() => tags.filter((tag) => (tag.simCount ?? 0) > 0), [tags]);

    const error = tagsError || statusesError;
    const isLoading = tagsLoading || statusesLoading;

    const rawSelected = getValues('tags');

    const selectedOptions = useMemo(() => {
      if (!rawSelected || rawSelected.length === 0) {
        return rawSelected;
      }

      return rawSelected.filter((tagId) => validTags.find((tag) => `${tag.id}` === tagId));
    }, [rawSelected, validTags]);

    useEffect(() => {
      if (!tagsLoading && (selectedOptions?.length ?? 0) !== (rawSelected?.length ?? 0)) {
        onClear();
      }
    }, [onClear, rawSelected, selectedOptions, storedSimTags, tags, tagsLoading]);

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(() => {
      submit();
    }, [submit]);

    const checkboxField = register('tags', {
      onChange: handleChange,
    });

    const handleAllChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        if (!setValue) {
          // eslint-disable-next-line no-console
          console.error('Unexpectedly missing setValue when toggling select all');
          return;
        }
        if (e.target.checked) {
          setValue(
            'tags',
            validTags.map((tag) => `${tag.id}`)
          );
        } else {
          setValue('tags', undefined);
        }
        submit();
      },
      [setValue, submit, validTags]
    );

    const handleNoTagChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        if (!setValue) {
          // eslint-disable-next-line no-console
          console.error('Unexpectedly missing setValue when toggling no tags');
          return;
        }

        setValue('noTags', e.target.checked);
        submit();
      },
      [setValue, submit]
    );

    const isSet =
      (!!storedSimTags && isArray(storedSimTags) && storedSimTags.length > 0) ||
      !!getValues('noTags');

    const content = useMemo(() => {
      if (error) {
        return (
          <InlineNotification variant="system-error" style={{ display: 'inline' }}>
            We ran into an issue loading this filter. You can try to refresh.
          </InlineNotification>
        );
      }
      if (isLoading) {
        return <div className={styles.placeholderText}>Loading...</div>;
      }
      if (!hasOrganizationId) {
        return <div className={styles.placeholderText}>Please select an organization</div>;
      }
      if (!validTags || validTags.length === 0) {
        return <div className={styles.placeholderText}>No tags available</div>;
      }
      return (
        <>
          <Checkbox
            label="Select all"
            value="all"
            key="all"
            onChange={handleAllChange}
            indeterminate={
              (selectedOptions?.length ?? 0) > 0 && selectedOptions!.length !== validTags.length
            }
            defaultChecked={isSet && (selectedOptions?.length ?? 0) === validTags.length}
          />
          <hr className={filterStyles.divider} />
          <div className={styles.optionWrapper}>
            {validTags.map((tag) => (
              <Checkbox
                className={styles.tagOption}
                label={`${tag.name} (${tag.simCount})`}
                title={`${tag.name} (${tag.simCount})`}
                value={`${tag.id}`}
                key={tag.id}
                defaultChecked={isSet && !!tag.id && storedSimTags?.includes(tag.id)}
                {...checkboxField}
              />
            ))}
          </div>
          <hr className={filterStyles.divider} />
          <Checkbox
            label="SIMs with no tags"
            value="notags"
            key="notags"
            onChange={handleNoTagChange}
            defaultChecked={isSet && getValues('noTags')}
          />
        </>
      );
    }, [
      checkboxField,
      error,
      getValues,
      handleAllChange,
      handleNoTagChange,
      hasOrganizationId,
      isLoading,
      isSet,
      selectedOptions,
      storedSimTags,
      validTags,
    ]);

    return (
      <FilterAccordion
        onClear={onClear}
        label={filterLabels.tags}
        isSet={isSet}
        defaultActive={isSet}
      >
        <div className={styles.wrapper}>{content}</div>
      </FilterAccordion>
    );
  }
);

export default SimTagFilter;
